import React from 'react';
import { Send, RotateCcw } from 'lucide-react';
import { apiFetchRaw } from '../apiClient';
import { PeterMark, PeterPortrait } from './PeterAvatar';
import { topicById } from './helpContent';

// Same flags HelpDrawer uses. Passed to the API so Peter doesn't describe a screen that isn't there.
const DISABLED_FEATURES = [
  process.env.REACT_APP_RESOURCES_ENABLED === 'true' ? null : 'resources',
  process.env.REACT_APP_SCHEDULING_ENABLED === 'true' ? null : 'scheduling',
].filter(Boolean);

const OPENERS = [
  'How do I unsubmit a week?',
  'What does the RAG status mean?',
  'Who approves my timesheet?',
];

/**
 * Renders one line of Peter's answer, turning `[[topic:id]]` into a link into the help drawer.
 *
 * The id is resolved against the real corpus here rather than trusted from the API: the server has a
 * generated copy, and a citation the drawer can't open is worse than no citation. Unknown ids are
 * dropped silently.
 */
function AnswerLine({ text, onOpenTopic }) {
  const parts = text.split(/(\[\[topic:[a-z0-9-]+\]\])/gi);
  return parts.map((part, i) => {
    const match = /^\[\[topic:([a-z0-9-]+)\]\]$/i.exec(part);
    if (!match) return <React.Fragment key={i}>{part}</React.Fragment>;

    const topic = topicById(match[1].toLowerCase());
    if (!topic) return null;
    return (
      <button key={i} type="button" className="peter-cite" onClick={() => onOpenTopic(topic.id)}>
        {topic.title}
      </button>
    );
  });
}

function Bubble({ message, onOpenTopic }) {
  if (message.role === 'user') {
    return <div className="peter-bubble peter-bubble--user">{message.content}</div>;
  }
  return (
    <div className="peter-row">
      <PeterMark size={28} state={message.pending ? 'thinking' : message.failed ? 'error' : 'idle'} />
      <div className="peter-bubble peter-bubble--peter">
        {message.content
          ? message.content.split('\n').map((line, i) => (
              <p key={i} className="peter-line">
                <AnswerLine text={line} onOpenTopic={onOpenTopic} />
              </p>
            ))
          : <span className="peter-typing"><i /><i /><i /></span>}
      </div>
    </div>
  );
}

/**
 * `active` tells this component whether it is currently the thing on screen. It stays mounted
 * permanently inside PeterWidget so a conversation survives collapsing the panel and navigating the
 * app — so "just mounted" no longer means "just became visible", and the old mount-time focus grab
 * would otherwise steal keyboard focus from whatever page loaded underneath a collapsed, hidden chat.
 */
function PeterChat({ route, onOpenTopic, active = true }) {
  const [messages, setMessages] = React.useState([]);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const endRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => { if (active) inputRef.current?.focus(); }, [active]);
  React.useEffect(() => { if (active) endRef.current?.scrollIntoView({ block: 'end' }); }, [messages, active]);

  const ask = React.useCallback(async (question) => {
    const trimmed = question.trim();
    if (!trimmed || busy) return;

    // History is whatever is already on screen — the server holds no conversation state, so what
    // the user can see is exactly what Peter is given.
    const history = messages.map(({ role, content }) => ({ role, content }));

    setDraft('');
    setBusy(true);
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: trimmed },
      { role: 'assistant', content: '', pending: true },
    ]);

    const patchLast = (patch) =>
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], ...patch };
        return next;
      });

    try {
      const res = await apiFetchRaw('/ai/peter/chat', {
        method: 'POST',
        body: JSON.stringify({ question: trimmed, history, route, disabledFeatures: DISABLED_FEATURES }),
      });
      if (!res.ok) throw new Error(`Peter is unavailable (${res.status}).`);

      // NDJSON: one JSON object per line, so hold the partial tail between chunks.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let answer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim()) continue;
          let event;
          try { event = JSON.parse(line); } catch { continue; }

          if (event.type === 'delta') {
            answer += event.text;
            patchLast({ content: answer, pending: false });
          } else if (event.type === 'error') {
            // A 200 already went out, so failures arrive in-band rather than as a status code.
            patchLast({ content: event.message, pending: false, failed: true });
          }
        }
      }

      if (!answer.trim()) patchLast({ pending: false });
    } catch (err) {
      patchLast({
        content: err.message || 'Peter could not answer that just now.',
        pending: false,
        failed: true,
      });
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }, [busy, messages, route]);

  const onKeyDown = (e) => {
    // Enter sends; Shift+Enter is a newline. A question long enough to need paragraphs is rare
    // enough that the fast path should be the common one.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask(draft);
    }
  };

  return (
    <div className="peter-chat">
      <div className="peter-log">
        {messages.length === 0 ? (
          <div className="peter-welcome">
            <PeterPortrait size={104} />
            <div className="peter-welcome-name">Peter the Project Monkey</div>
            <p className="peter-welcome-copy">
              Ask me how the Hub works and I'll point you at the right screen. I can also look up logged
              hours by project, category or person, and monthly allocation.
            </p>
            <div className="peter-openers">
              {OPENERS.map((q) => (
                <button key={q} type="button" className="peter-opener" onClick={() => ask(q)}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => <Bubble key={i} message={m} onOpenTopic={onOpenTopic} />)
        )}
        <div ref={endRef} />
      </div>

      <div className="peter-composer">
        <textarea
          ref={inputRef}
          className="peter-input"
          rows={1}
          placeholder="Ask Peter…"
          value={draft}
          maxLength={2000}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Ask Peter a question"
        />
        <button
          type="button"
          className="peter-send"
          onClick={() => ask(draft)}
          disabled={busy || !draft.trim()}
          aria-label="Send"
        >
          <Send size={15} strokeWidth={2} aria-hidden />
        </button>
      </div>

      {messages.length > 0 && (
        <button type="button" className="peter-reset" onClick={() => setMessages([])} disabled={busy}>
          <RotateCcw size={12} strokeWidth={2} aria-hidden /> Start over
        </button>
      )}
    </div>
  );
}

export default PeterChat;
